import React, {useEffect, useState} from 'react'
import {useParams} from 'react-router-dom'
import LoaderComponent from '../../components/LoaderComponent'
import axios from 'axios'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import {toast} from 'react-toastify'
import DataTableList from '../../components/DataTableList'
import {DOCTOR_PAYMENT_HISTORY_HEADERS} from '../../../../constants/RoleConstants'
import {useReduxAuth} from '../../../auth'
import {filterHeadersByUserRole} from '../../helper/helperFunction'
import MetaTags from '../../../../meta/MetaTags'

// Define interface for invoice line items
interface InvoiceLineItem {
  id: string
  amount: number
  currency: string
  description: string
  quantity: number
  period: {
    start: number
    end: number
  }
  pricing?: {
    unit_amount_decimal: string
  }
}

// Define interface for an invoice
interface Invoice {
  id: string
  number: string
  created: number
  due_date: number | null
  status: string
  total: number
  currency: string
  customer_name: string
  customer_email: string
  hosted_invoice_url: string
  invoice_pdf: string
  lines: {
    data: InvoiceLineItem[]
  }
  subscription?: string
  period_start: number
  period_end: number
}

const DoctorPaymentHistory: React.FC = () => {
  const {currentUser} = useReduxAuth()
  const {doctorId} = useParams<{doctorId: string}>()
  const [doctorInvoices, setDoctorInvoices] = useState<Invoice[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)

  // Fetch all Stripe transactions for this doctor
  const fetchData = async () => {
    setIsLoading(true)
    try {
      const response = await axios.get(API_ENDPOINTS.GET_ALL_TRANSACTIONS_FROM_STRIPE(doctorId!))
      console.log('API Response:', response.data) // Debug log
      const invoicesData: Invoice[] = response.data?.data?.invoices || []
      console.log('Invoices extracted:', invoicesData) // Debug log
      setDoctorInvoices(invoicesData)
    } catch (error: any) {
      console.error('API Error:', error) // Debug log
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.message || 'Something went wrong')
      } else if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error('An unexpected error occurred')
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Format raw Stripe invoices into table rows
  const formatInvoicesForDisplay = (invoices: Invoice[]) =>
    invoices.map((invoice) => {
      const items = invoice.lines.data
        .map((line) => {
          // Extract the base description before " (at" if it exists
          const baseDescription = line.description.split(' (at')[0]
          return `${line.quantity} × ${baseDescription}`
        })
        .join(', ')

      // Get the billing period from the first line item's period (actual service period)
      const firstLineItem = invoice.lines.data[0]
      const billingPeriod = firstLineItem?.period
        ? `${new Date(firstLineItem.period.start * 1000).toLocaleDateString()} - ${new Date(
            firstLineItem.period.end * 1000
          ).toLocaleDateString()}`
        : `${new Date(invoice.period_start * 1000).toLocaleDateString()} - ${new Date(
            invoice.period_end * 1000
          ).toLocaleDateString()}`

      return {
        id: invoice.id,
        invoiceNumber: invoice.number,
        date: new Date(invoice.created * 1000).toLocaleDateString(),
        amount: `$${(invoice.total / 100).toFixed(2)} ${invoice.currency.toUpperCase()}`,
        status: invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1),
        items: items || 'No items',
        customerName: invoice.customer_name || 'N/A',
        viewInvoice: invoice.hosted_invoice_url,
        downloadPdf: invoice.invoice_pdf,
        billingPeriod: billingPeriod,
      }
    })

  // On mount (or doctorId change), load data
  useEffect(() => {
    fetchData()
  }, [doctorId])

  if (isLoading) return <LoaderComponent />

  const formattedData = formatInvoicesForDisplay(doctorInvoices)
  console.log(formattedData)

  // Only show columns currentUser is allowed to see
  const headers = filterHeadersByUserRole(DOCTOR_PAYMENT_HISTORY_HEADERS, currentUser)

  return (
    <>
      <MetaTags title='Payment History' />
      <div className='card mb-5 mb-xl-10'>
        <div className='card-header d-flex justify-content-between align-items-center'>
          <h3 className='fw-bolder m-0'>Payment History</h3>
        </div>
        <div className='card-body p-9'>
          {doctorInvoices.length === 0 ? (
            <div className='text-center py-5'>
              <i className='fas fa-file-invoice-dollar fa-3x text-muted mb-3'></i>
              <p className='text-muted'>No invoices found for this account.</p>
            </div>
          ) : (
            <DataTableList
              columns={headers}
              data={formattedData}
              showAddButton={false}
              showHeader={true}
            />
          )}
        </div>
      </div>
    </>
  )
}

export default DoctorPaymentHistory
